import { useRouteError, Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, Home, RefreshCcw, Mail, ArrowLeft } from 'lucide-react'

export function ErrorPage() {
  const error = useRouteError()
  const navigate = useNavigate()
  
  const is404 = error?.status === 404
  const errorMessage = error?.statusText || error?.message || 'Something went wrong'
  
  const handleRefresh = () => {
    window.location.reload()
  }
  
  const handleGoBack = () => {
    navigate(-1)
  }

  return (
    <div className="min-h-screen bg-background-dark flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none opacity-30 bg-[linear-gradient(to_right,rgba(227,255,4,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(227,255,4,0.03)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-red-500/10 blur-[120px] rounded-full pointer-events-none"></div>
      
      <div className="relative z-10 text-center max-w-lg">
        {/* Error Icon */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 sm:w-32 sm:h-32 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/30 animate-pulse">
              <AlertTriangle className="w-12 h-12 sm:w-16 sm:h-16 text-red-500" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-card-dark border border-border-dark rounded-full flex items-center justify-center">
              <span className="text-lg">😵</span>
            </div>
          </div>
        </div>

        {/* Error Code */}
        <h1 className="text-6xl sm:text-8xl font-black text-white mb-4 tracking-tighter">
          {is404 ? '404' : 'Oops!'}
        </h1>

        {/* Error Title */}
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
          {is404 ? 'Page Not Found' : 'Something Went Wrong'}
        </h2>

        {/* Error Message */}
        <p className="text-slate-400 mb-8 leading-relaxed">
          {is404 
            ? "The page you're looking for doesn't exist or has been moved. Let's get you back on track!"
            : "We encountered an unexpected error. Don't worry, our team has been notified and is working on it."
          }
        </p>

        {/* Error Details (collapsible for non-404) */}
        {!is404 && errorMessage && (
          <div className="mb-8 bg-card-dark/50 border border-red-500/20 rounded-xl p-4 text-left">
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Error Details</p>
            <p className="text-red-400 text-sm font-mono break-all">{errorMessage}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center gap-2 bg-white/5 border border-border-dark text-white font-bold px-6 py-3 rounded-xl hover:bg-white/10 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
          
          <Link
            to="/"
            className="flex items-center justify-center gap-2 bg-game-primary text-black font-bold px-6 py-3 rounded-xl hover:scale-105 transition-all"
          >
            <Home className="w-5 h-5" />
            Back to Home
          </Link>
          
          {!is404 && (
            <button
              onClick={handleRefresh}
              className="flex items-center justify-center gap-2 bg-white/5 border border-border-dark text-white font-bold px-6 py-3 rounded-xl hover:bg-white/10 transition-all"
            >
              <RefreshCcw className="w-5 h-5" />
              Try Again
            </button>
          )}
        </div>

        {/* Contact Support */}
        <div className="bg-card-dark border border-border-dark rounded-2xl p-6">
          <p className="text-slate-400 text-sm mb-4">
            Still having trouble? We're here to help!
          </p>
          <a
            href="mailto:support@polytoria.com"
            className="inline-flex items-center gap-2 text-game-primary hover:underline font-medium"
          >
            <Mail className="w-4 h-4" />
            support@polytoria.com
          </a>
        </div>

        {/* Decorative Elements */}
        <div className="mt-12 flex justify-center gap-2">
          <div className="w-2 h-2 bg-game-primary/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-game-primary/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-game-primary/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  )
}

export default ErrorPage
